import { createServerFn } from "@tanstack/react-start";
import z from "zod";
import { getStateCodeFromLabel } from "~/lib/add-location/state-options";
import { lookupCmsLocationPlaceId } from "~/lib/search/cms-location-catalog";
import { isAllowedMapsBrowserOrigin } from "./maps-request-guard";

/** Cap CMS/free-text loc resolution to limit fan-out. */
const MAX_SEARCH_LOCATION_LABELS = 8;
const MAX_SEARCH_LOCATION_LABEL_LENGTH = 120;

export type ResolvedSearchLocationLabels = {
  location: string[];
  placeIds: string[];
};

/**
 * Resolve CMS/`?loc=` labels from the offline city catalog and state list.
 *
 * Does not call Google. Catalog misses are dropped (not emitted with an empty
 * Place ID) so search cannot fall through to Geocoding for crawled junk.
 *
 * State labels (Bayern, …) keep an empty placeId — server search filters those
 * by state code. City names that are also states (Berlin, Hamburg, Bremen)
 * resolve from the catalog first so CityLinks stay city-scoped.
 */
export function resolveSearchLocationLabelsLocal(
  rawLabels: string[],
): ResolvedSearchLocationLabels {
  const location: string[] = [];
  const placeIds: string[] = [];

  const labels = Array.from(
    new Map(
      rawLabels
        .slice(0, MAX_SEARCH_LOCATION_LABELS)
        .map((label) => label.slice(0, MAX_SEARCH_LOCATION_LABEL_LENGTH))
        .map((label) => [label.trim().toLowerCase(), label] as const),
    ).values(),
  );

  for (const raw of labels) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    const catalogPlaceId = lookupCmsLocationPlaceId(trimmed);
    if (catalogPlaceId) {
      location.push(trimmed);
      placeIds.push(catalogPlaceId);
      continue;
    }

    if (getStateCodeFromLabel(trimmed)) {
      location.push(trimmed);
      placeIds.push("");
    }
  }

  return { location, placeIds };
}

/**
 * Resolve free-text search labels (e.g. CMS `?loc=Berlin`) to Places `placeId`s
 * so search can use Place Details instead of Geocoding.
 */
export const resolveSearchLocationLabels = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      labels: z.preprocess(
        (raw) => {
          if (!Array.isArray(raw)) return raw;
          return raw
            .slice(0, MAX_SEARCH_LOCATION_LABELS)
            .map((item) =>
              typeof item === "string"
                ? item.slice(0, MAX_SEARCH_LOCATION_LABEL_LENGTH)
                : item,
            );
        },
        z
          .array(z.string().max(MAX_SEARCH_LOCATION_LABEL_LENGTH))
          .max(MAX_SEARCH_LOCATION_LABELS),
      ),
    }),
  )
  .handler(async (ctx): Promise<ResolvedSearchLocationLabels> => {
    if (
      !isAllowedMapsBrowserOrigin({ requireOriginInProduction: true })
    ) {
      return { location: [], placeIds: [] };
    }
    return resolveSearchLocationLabelsLocal(ctx.data.labels);
  });
